import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '../utils.ts';

type Variant = 'primary' | 'secondary' | 'ghost' | 'inverse' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: Variant;
  size?: Size;
  asChild?: boolean;
  children: ReactNode;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-button font-semibold ' +
  'transition-all duration-fast ease-clickt ' +
  'focus-visible:outline-none focus-visible:shadow-focus-ring ' +
  'disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ' +
  'motion-reduce:transform-none whitespace-nowrap';

const variants: Record<Variant, string> = {
  primary:
    'bg-electric text-white hover:bg-sapphire hover:-translate-y-px active:translate-y-0 shadow-soft hover:shadow-hover',
  secondary:
    'bg-surface text-midnight border border-midnight/10 hover:border-midnight/30 hover:-translate-y-px',
  ghost: 'bg-transparent text-midnight hover:bg-light-silver',
  inverse: 'bg-white text-ink hover:bg-white/90 hover:-translate-y-px shadow-soft',
  danger: 'bg-danger text-white hover:bg-danger/90 shadow-soft hover:shadow-hover',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3 text-[13px]',
  md: 'h-11 px-4 text-[14px]',
  lg: 'h-14 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', asChild = false, className, children, ...rest },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref as never}
      className={cn(base, variants[variant], sizes[size], className)}
      {...rest}
    >
      {children}
    </Comp>
  );
});
